import {
    useState,
    useEffect,
    useContext,
    Fragment,
    useMemo,
    useCallback,
    useRef,
} from 'react';
import { Grid, Box, Button, Divider, Skeleton } from '@mui/material';
import {
    format,
    parseISO,
    addMinutes,
    isBefore,
    addDays,
    getDay,
    differenceInMinutes,
} from 'date-fns';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import SchoolIcon from '@mui/icons-material/School';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import CancelPresentationIcon from '@mui/icons-material/CancelPresentation';
import PeopleIcon from '@mui/icons-material/People';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { useTranslation } from 'react-i18next';
import Booking from '../booking/Booking';
import ActivityClassDialog from '../../sc/activities/ActivityClassDialog';
import CreateBookingScheduleDialog from '../../../components/dialogs/CreateBookingScheduleDialog';
import BusyScheduleDialog from '../../../components/dialogs/BusyScheduleDialog';
import SeatingDialog from '../../sc/timetable/SeatingDialog';
import TimetableBookingsDialog from './TimetableBookingsDialog';
import { AppContext, SttDialog, SttFormSelect } from '../../../sporttia/all';
import PieceCapacityDialog from '../../sc/timetable/PieceCapacityDialog';
import { formatPriceByLocale, parseToDate } from '../../../lib/utils';
import useTimetableService from '../../../services/TimetableService';
import { shouldForceFullRatesForGame } from '../../../lib/GameUtils';
import {
    canPasteBooking,
    filterTimetableActions,
} from './timetableOperatePermissions';

const skeletonStyles = {
    button: {
        height: 36,
        borderRadius: 4,
        width: '100%',
    },
    buttonOutlined: {
        backgroundColor: 'transparent',
        border: '1px solid rgba(0, 0, 0, 0.12)',
    },
};

/**
 * Timetable operate
 * =================
 *
 */
export default function TimetableOperate({
    sc,
    column,
    selectedPiece,
    bookingCut = null,
    setBookingCut,
    onClose,
    onChangeTimetable,
    cutBooking = null,
    privileged = false,
}: any) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    const [bookingAttempt, setBookingAttempt] = useState<any>(null);
    const [createClass, setCreateClass] = useState<any>(null);
    const [createFixedBooking, setCreateFixedBooking] = useState<any>(null);
    const [createBusySchedule, setCreateBusySchedule] = useState<any>(null);
    const [openSeatingDialog, setOpenSeatingDialog] = useState(false);
    const [openBookingsDialog, setOpenBookingsDialog] = useState(false);
    const [openBusySchedule, setOpenBusySchedule] = useState(false);
    const [openFixedBooking, setOpenFixedBooking] = useState(false);
    const [openPieceCapacityDialog, setOpenPieceCapacityDialog] =
        useState(false);
    const [piece, setPiece] = useState(selectedPiece);
    const [timetableParams, setTimetableParams] = useState({
        dateIni: selectedPiece?.ini || '',
        dateEnd: selectedPiece?.end || '',
        fieldId: column?.facility?.id || 0,
    });

    const {
        useGetTimetableRates,
        useGetTimetableOccupations,
        useGetTimetablePrivileges,
    } = useTimetableService();

    // Enable queries only when we have valid params
    const queryEnabled = !!(
        timetableParams.dateIni &&
        timetableParams.dateEnd &&
        timetableParams.fieldId
    );

    const { isLoading: loadingPieceOccupations, data: occupationsData } =
        useGetTimetableOccupations(timetableParams, {
            enabled: queryEnabled,
            staleTime: 30000, // 30 seconds - prevents duplicate fetches
        } as any);

    const { isLoading: loadingPieceRates, data: ratesData } =
        useGetTimetableRates(timetableParams, {
            enabled: queryEnabled,
            staleTime: 30000,
        } as any);

    const { isLoading: loadingPiecePrivileges, data: privilegesData } =
        useGetTimetablePrivileges(timetableParams, {
            enabled: queryEnabled,
            staleTime: 30000,
        } as any);

    // Handle occupations data
    useEffect(() => {
        if (occupationsData) {
            setPiece((prev: any) => ({ ...prev, bookings: occupationsData }));
        }
    }, [occupationsData]);

    // Handle rates data
    useEffect(() => {
        if (ratesData) {
            setPiece((prev: any) => ({ ...prev, timetableRates: ratesData }));
        }
    }, [ratesData]);

    // Handle privileges data
    useEffect(() => {
        if (privilegesData) {
            setPiece((prev: any) => ({ ...prev, privilegesData }));
        }
    }, [privilegesData]);

    // Sync piece.ini/piece.end with timetableParams so that
    // "Consultar aforo" and "Reservas" always use the same dates
    useEffect(() => {
        setPiece((prev: any) => ({
            ...prev,
            ini: timetableParams.dateIni,
            end: timetableParams.dateEnd,
        }));
    }, [timetableParams.dateIni, timetableParams.dateEnd]);

    const isRefetching =
        loadingPiecePrivileges && loadingPieceRates && loadingPieceOccupations;

    /**
     * Im sc owner
     */
    const ImSCOwner = useMemo(() => {
        if (cxt.sc && sc) {
            return (
                cxt.user?.role === cxt.constants.roleSC &&
                parseInt(String(cxt.sc?.id)) === parseInt(String(sc.id))
            );
        }
        return false;
    }, [cxt.user?.role, cxt.sc, sc, cxt.constants.roleSC]);

    /**
     * Open create booking
     */
    const openCreateBooking = useCallback(() => {
        const now = new Date();
        const formattedNow = format(now, "yyyy-MM-dd'T'HH':00:00'");
        setBookingAttempt({
            ini: formattedNow,
            end: formattedNow,
            facility: column.facility,
        });
    }, [column.facility]);

    /**
     * Open creating class
     */
    const openCreateClass = useCallback(() => {
        setCreateClass({
            id: null,
            status: 'ACTIVE',
            ini: piece?.ini,
            end: piece?.end,
            facility: column.facility
                ? { id: column.facility.id, name: column.facility.name }
                : null,
            weekdays: piece?.ini ? [getDay(parseISO(piece.ini))] : [],
        });
    }, [piece?.ini, piece?.end, column.facility]);

    /**
     * If 'timeini' or 'timeend' was changed, createBusySchedule and createFixedBooking object will be update.
     */
    useEffect(() => {
        const dateIniParsed = parseISO(timetableParams.dateIni);
        const dateEndParsed = parseISO(timetableParams.dateEnd);

        setCreateBusySchedule({
            idFacility: column.facility ? column.facility.id : null,
            status: 'ACTIVE',
            name: t('timetable.closed'),
            ini: format(dateIniParsed, 'yyyy-MM-dd'),
            end: format(dateEndParsed, 'yyyy-MM-dd'),
            timeini: format(dateIniParsed, 'HH:mm'),
            timeend: format(dateEndParsed, 'HH:mm'),
        });

        setCreateFixedBooking({
            name: '',
            status: 'ACTIVE',
            dateini: format(dateIniParsed, 'yyyy-MM-dd'),
            dateend: format(dateEndParsed, 'yyyy-MM-dd'),
            timeini: format(dateIniParsed, 'HH:mm'),
            timeend: format(dateEndParsed, 'HH:mm'),
            facility: column.facility
                ? { id: column.facility.id, name: column.facility.name }
                : null,
            user: null,
            weekdays: [getDay(dateIniParsed)],
        });
        // Note: refetch is handled automatically by react-query when timetableParams change
    }, [timetableParams.dateIni, timetableParams.dateEnd]);

    /**
     * Paste booking
     *
     * params
     *
     * 	- facility
     * 	- piece
     */
    const pasteBooking = useCallback(
        (
            p: { piece: any; facility: any } = { piece: null, facility: null },
        ) => {
            if (!bookingCut) return;

            // Calculate the duration difference in minutes
            const bookingIni = parseISO(bookingCut.ini);
            const bookingEnd = parseISO(bookingCut.end);
            const diffMinutes = differenceInMinutes(bookingEnd, bookingIni);

            // Recalculate times...
            const newStart = parseISO(p.piece.ini);
            let newEnd = addMinutes(newStart, diffMinutes);

            if (isBefore(newEnd, newStart)) {
                newEnd = addDays(newEnd, 1);
            }

            cxt.api('PUT', `/bookings/${bookingCut.id}`, {
                params: {
                    idFacility: p.facility.id,
                    ini: format(newStart, 'yyyy-MM-dd HH:mm:ss'),
                    end: format(newEnd, 'yyyy-MM-dd HH:mm:ss'),
                },
                success: () => {
                    onChangeTimetable();
                    onClose();
                    setBookingCut(null);
                },
            });
        },
        [bookingCut, cxt, onChangeTimetable, onClose, setBookingCut],
    );

    /**
     * Piece changes
     */
    const actions = useMemo(() => {
        if (!piece.privilegesData?.actions) return [];

        // Acciones del backend para la pieza que además permite el rol v8
        // del operador (con los roles apagados, todas las del backend).
        const allowedActions = filterTimetableActions(
            piece.privilegesData.actions,
            cxt,
        );

        return [
            {
                condition: () => allowedActions.includes('book'),
                caption: t('timetable.book'),
                onClick: () => openCreateBooking(),
                icon: <ConfirmationNumberIcon />,
            },
            {
                condition: () => allowedActions.includes('createFixedBooking'),
                caption: t('timetable.createFixedBook'),
                onClick: () => setOpenFixedBooking(true),
                icon: <ViewModuleIcon />,
            },
            {
                condition: () => allowedActions.includes('createClass'),
                caption: t('timetable.createClass'),
                onClick: () => openCreateClass(),
                icon: <SchoolIcon />,
            },
            {
                condition: () => allowedActions.includes('closeFacilities'),
                caption: t('timetable.closeFacility'),
                onClick: () => setOpenBusySchedule(true),
                icon: <CancelPresentationIcon />,
            },
            {
                condition: () => bookingCut !== null && canPasteBooking(cxt),
                caption: t('timetable.pasteBooking'),
                onClick: () =>
                    pasteBooking({
                        facility: column.facility,
                        piece,
                    }),
                icon: <FileCopyIcon />,
            },
            {
                separator: true,
                type: 'consult',
                condition: () =>
                    !Number.isNaN(piece?.capacity?.total) &&
                    !Number.isNaN(piece?.capacity?.free) &&
                    piece?.capacity?.total > piece?.capacity?.free &&
                    ImSCOwner,
                caption: t('timetable.consultSeating'),
                onClick: () => setOpenSeatingDialog(true),
                icon: <PeopleIcon />,
            },
            {
                type: 'consult',
                condition: () =>
                    !Number.isNaN(piece?.capacity?.total) &&
                    !Number.isNaN(piece?.capacity?.free) &&
                    piece?.capacity?.total > piece?.capacity?.free &&
                    ImSCOwner &&
                    piece?.bookings?.rows?.length > 0,
                caption: t('labels.bookings'),
                onClick: () => setOpenBookingsDialog(true),
                icon: <ClearAllIcon />,
            },
            {
                type: 'consult',
                condition: () =>
                    piece.bookings && piece.bookings.rows?.length && privileged,
                caption: t('timetable.consultSeating'),
                onClick: () => setOpenPieceCapacityDialog(true),
                icon: <PeopleIcon />,
            },
        ];
    }, [
        bookingCut,
        ImSCOwner,
        privileged,
        piece,
        column?.facility,
        cxt,
        openCreateBooking,
        openCreateClass,
        pasteBooking,
    ]);

    const initializedRef = useRef(false);

    const freePieces = column.pieces.filter((pc: any) => pc.mark === 'FREE');
    const onlyOnePiece = freePieces.length === 1;

    function getEffectiveDuration(pieceArg: any) {
        const ratesFacility = pieceArg?.timetableRates?.facility;
        if (
            ratesFacility?.defaultDuration &&
            ratesFacility.defaultDuration > 0
        ) {
            return ratesFacility.defaultDuration;
        }
        return pieceArg.duration;
    }

    // Picks the dateEnd matching ini + duration against existing FREE pieces.
    // Date timestamps are compared (not strings) so backend "yyyy-MM-dd HH:mm:ss"
    // and ISO "yyyy-MM-dd'T'HH:mm:ss" both match. Falls back to the starting
    // piece's own end when the target exceeds available consecutive FREE slots,
    // ensuring the value always exists in the dateEnd select options.
    function pickDateEnd(
        iniIso: string,
        duration: number,
        pieces: any[],
    ): string {
        const startingPiece = pieces.find((pc: any) => pc.ini === iniIso);
        const fallback = startingPiece?.end ?? iniIso;
        const iniDate = parseToDate(iniIso);
        if (!iniDate) return fallback;

        const targetTs = addMinutes(iniDate, duration).getTime();
        const exactMatch = pieces.find((pc: any) => {
            if (pc.mark !== 'FREE') return false;
            const pcEnd = parseToDate(pc.end);
            return pcEnd ? pcEnd.getTime() === targetTs : false;
        });
        return exactMatch?.end ?? fallback;
    }

    useEffect(() => {
        if (!piece) return;

        if (!initializedRef.current) {
            if (onlyOnePiece && piece.mark === 'FREE') {
                setTimetableParams({
                    dateIni: freePieces[0].ini,
                    dateEnd: freePieces[0].end,
                    fieldId: column.facility.id,
                });
            } else {
                const duration = getEffectiveDuration(piece);
                const validEnd = pickDateEnd(
                    piece.ini,
                    duration,
                    column.pieces,
                );

                setTimetableParams((prev: any) => ({
                    ...prev,
                    dateIni: piece.ini,
                    dateEnd: validEnd,
                }));
            }

            initializedRef.current = true;
        }
    }, [piece, column?.pieces]);

    // Render
    return (
        <>
            <SttDialog
                title={`${column.facility.name}, ${format(
                    parseISO(piece.ini),
                    'd-MM-yyyy',
                )}`}
                open={['FREE', 'FULL'].includes(piece.mark)}
                onClose={() => {
                    initializedRef.current = false;
                    onClose();
                }}
                content={
                    <>
                        <Grid
                            container
                            alignItems="center"
                            justifyContent="center"
                            spacing={3}
                        >
                            {/* --- Time ini --- */}
                            <SttFormSelect
                                grid
                                xs={12}
                                sm={6}
                                md={6}
                                disabled={piece.mark !== 'FREE'}
                                defVal={timetableParams.dateIni}
                                onChange={({ value }: any) => {
                                    const duration =
                                        getEffectiveDuration(piece);
                                    const validEnd = pickDateEnd(
                                        value,
                                        duration,
                                        column.pieces,
                                    );

                                    setTimetableParams((prev: any) => ({
                                        ...prev,
                                        dateIni: value,
                                        dateEnd: validEnd,
                                    }));
                                    // Note: react-query auto-refetches when timetableParams change
                                }}
                                options={column.pieces
                                    .filter((pc: any) => pc.mark === 'FREE')
                                    .map((pc: any) => ({
                                        caption: format(
                                            parseISO(pc.ini),
                                            'H:mm',
                                        ),
                                        value: pc.ini,
                                    }))}
                            />

                            {/* --- Time end --- */}
                            <SttFormSelect
                                grid
                                xs={12}
                                sm={6}
                                md={6}
                                disabled={piece.mark !== 'FREE'}
                                defVal={timetableParams.dateEnd}
                                onChange={({ value }: any) => {
                                    setTimetableParams((prev: any) => ({
                                        ...prev,
                                        dateEnd: value,
                                    }));
                                }}
                                options={column.pieces
                                    .filter((pc: any) => pc.mark === 'FREE')
                                    .map((pc: any) => ({
                                        caption: format(
                                            parseISO(pc.end),
                                            'H:mm',
                                        ),
                                        value: pc.end,
                                    }))}
                            />
                        </Grid>

                        {/* --- Render all actions --- */}
                        <Box mt={3}>
                            {isRefetching ? (
                                <>
                                    {[1, 2, 3, 4].map((i: any) => (
                                        <Box
                                            my={2}
                                            key={`action-skeleton-${i}`}
                                            display="flex"
                                            alignItems="center"
                                        >
                                            <Box flex={1}>
                                                <Skeleton
                                                    variant="rectangular"
                                                    height={36}
                                                    width="100%"
                                                    animation="wave"
                                                    style={
                                                        skeletonStyles.button
                                                    }
                                                />
                                            </Box>
                                        </Box>
                                    ))}

                                    <Box my={3}>
                                        <Divider />
                                    </Box>

                                    {[1, 2].map((i: any) => (
                                        <Box
                                            my={2}
                                            key={`action-skeleton-consult-${i}`}
                                            display="flex"
                                            alignItems="center"
                                        >
                                            <Box flex={1}>
                                                <Skeleton
                                                    variant="rectangular"
                                                    height={36}
                                                    width="100%"
                                                    animation="wave"
                                                    style={{
                                                        ...skeletonStyles.button,
                                                        ...skeletonStyles.buttonOutlined,
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    ))}
                                </>
                            ) : (
                                <Box my={5}>
                                    {actions &&
                                        actions.map((action: any) => {
                                            if (action.condition()) {
                                                return (
                                                    <Fragment
                                                        key={action.caption}
                                                    >
                                                        {/* --- Separator --- */}
                                                        {action.separator && (
                                                            <Box my={3}>
                                                                <Divider />
                                                            </Box>
                                                        )}

                                                        <Box my={2}>
                                                            <Button
                                                                fullWidth
                                                                variant={
                                                                    action.type ===
                                                                    'consult'
                                                                        ? 'outlined'
                                                                        : 'contained'
                                                                }
                                                                color="primary"
                                                                onClick={
                                                                    action.onClick
                                                                }
                                                                startIcon={
                                                                    action.icon || (
                                                                        <ArrowRightIcon />
                                                                    )
                                                                }
                                                            >
                                                                {action.caption}
                                                            </Button>
                                                        </Box>
                                                    </Fragment>
                                                );
                                            }
                                            return null;
                                        })}
                                </Box>
                            )}
                        </Box>

                        <Box mt={3}>
                            {isRefetching ? (
                                <>
                                    <Box mb={2}>
                                        <Skeleton
                                            variant="text"
                                            width="40%"
                                            height={20}
                                            animation="wave"
                                            style={{ borderRadius: 4 }}
                                        />
                                    </Box>
                                    {[1, 2].map((schedule: any) => (
                                        <Box
                                            mt={2}
                                            mb={0}
                                            key={`rate-skeleton-${schedule}`}
                                        >
                                            <Skeleton
                                                width="60%"
                                                height={24}
                                                animation="wave"
                                                style={{ borderRadius: 4 }}
                                            />
                                            {[1, 2].map((rate: any) => (
                                                <Box
                                                    key={`rate-detail-skeleton-${schedule}-${rate}`}
                                                    mt={1}
                                                    mb={0}
                                                    ml={2}
                                                >
                                                    <Skeleton
                                                        width="80%"
                                                        height={20}
                                                        animation="wave"
                                                        style={{
                                                            borderRadius: 4,
                                                        }}
                                                    />
                                                </Box>
                                            ))}
                                        </Box>
                                    ))}
                                </>
                            ) : (
                                <Box mt={3}>
                                    {piece.timetableRates &&
                                        piece.timetableRates.schedules.length >
                                            0 &&
                                        piece.timetableRates.schedules.map(
                                            (schedule: any) => (
                                                <Box mt={2} key={schedule.id}>
                                                    {t('timetable.horary')}:{' '}
                                                    <b>{schedule.name || ''}</b>
                                                    {schedule.rates &&
                                                        schedule.rates.length >
                                                            0 &&
                                                        schedule.rates.map(
                                                            (rate: any) => (
                                                                <Box
                                                                    key={
                                                                        rate.id
                                                                    }
                                                                >
                                                                    -{' '}
                                                                    <b>
                                                                        {
                                                                            rate.name
                                                                        }
                                                                    </b>{' '}
                                                                    -{' '}
                                                                    {formatPriceByLocale(
                                                                        rate?.price,
                                                                        rate
                                                                            ?.priceShape
                                                                            ?.currency ??
                                                                            piece
                                                                                ?.priceShape
                                                                                ?.currency,
                                                                        rate
                                                                            ?.priceShape
                                                                            ?.locale ??
                                                                            piece
                                                                                ?.priceShape
                                                                                ?.locale,
                                                                    )}{' '}
                                                                    /{' '}
                                                                    {
                                                                        rate.duration
                                                                    }{' '}
                                                                    m
                                                                </Box>
                                                            ),
                                                        )}
                                                </Box>
                                            ),
                                        )}
                                </Box>
                            )}
                        </Box>
                    </>
                }
                buttons={[
                    {
                        type: 'close',
                        onClick: onClose,
                    },
                ]}
            />

            {bookingAttempt && (
                <Booking
                    open={bookingAttempt !== null}
                    onClose={() => setBookingAttempt(null)}
                    idSC={sc.id}
                    column={column}
                    piece={piece}
                    ini={timetableParams.dateIni}
                    end={timetableParams.dateEnd}
                    onChangeTimetable={onChangeTimetable}
                    forceFullRates={shouldForceFullRatesForGame(
                        column.facility,
                        timetableParams.dateIni,
                    )}
                />
            )}

            {/* --- Create class / activity --- */}
            {createClass && (
                <ActivityClassDialog
                    open={createClass !== null}
                    item={createClass}
                    onSave={onChangeTimetable}
                    onClose={() => setCreateClass(null)}
                />
            )}

            {/* --- Create fixed booking --- */}
            {openFixedBooking && createFixedBooking && (
                <CreateBookingScheduleDialog
                    item={createFixedBooking}
                    onClose={() => setOpenFixedBooking(false)}
                    onSave={onChangeTimetable}
                />
            )}

            {/* --- Create busy --- */}
            {openBusySchedule && createBusySchedule && (
                <BusyScheduleDialog
                    item={createBusySchedule}
                    facility={column.facility}
                    onClose={() => setOpenBusySchedule(false)}
                    onSave={onChangeTimetable}
                />
            )}

            {/* --- Seatings --- */}
            {openSeatingDialog && (
                <SeatingDialog
                    open={openSeatingDialog}
                    facility={column.facility}
                    date={timetableParams.dateIni}
                    piece={piece}
                    onClose={() => setOpenSeatingDialog(false)}
                />
            )}

            {/* --- Booking list dialog --- */}
            {openBookingsDialog &&
                piece.bookings &&
                piece.bookings.rows.length > 0 && (
                    <TimetableBookingsDialog
                        open={openBookingsDialog}
                        bookings={piece.bookings.rows}
                        onClose={() => {
                            setOpenBookingsDialog(false);
                        }}
                        cutBooking={cutBooking}
                    />
                )}

            {/* --- PieceCapacityDialog (Only for users with '19' privilege) --- */}
            {openPieceCapacityDialog && (
                <PieceCapacityDialog
                    open={openPieceCapacityDialog}
                    facility={column.facility}
                    date={timetableParams.dateIni}
                    piece={piece}
                    onClose={() => setOpenPieceCapacityDialog(false)}
                />
            )}
        </>
    );
}
